import type { BrandId } from '@/lib/branding.ts'

/**
 * `brand` — the prose half of a brand profile, per locale (PRD-27 D-12, D-13, LOC-050).
 *
 * ── Why a profile was already a message catalogue ──
 *
 * `LoginPage.tsx` renders `brand.tagline` as the sign-in standfirst and `brandHeadHtml`
 * writes `brand.description` into `<meta name="description">`, which makes those two fields
 * user-visible prose that happened to live in a configuration table (F-13). They move here
 * so that `Messages = typeof en` really does cover one hundred percent of what a person can
 * read, and so a Gi user does not read an English sentence under a Portuguese sign-in form.
 *
 * ── What stayed in `profiles.ts`, and why ──
 *
 * `productName` ("Ralph Chat", "EVT Ralph", "Gi · Neoenergia"), `assistant` and the
 * non-reference `organisation` values are NAMES, and D-17 is explicit that brand and proper
 * nouns are never translated. Putting a name in a message catalogue and asking a translator
 * to leave it alone is how "Ralph Chat" becomes "Bate-papo do Ralph" in the tab title, so
 * they are not offered.
 *
 * `organisation` on the REFERENCE profile is the exception D-13 names: `'your organization'`
 * is a prose placeholder written to sit inside a sentence, not a company's name, and reading
 * it as a name is how a re-brand produces *"pergunte a your organization"*. That profile now
 * carries `organisation: null` — the same "unbranded" idiom `wordmark` and `avatar` already
 * use — and this entry is what is shown in its place.
 */

/** The reader-facing prose of one profile. Keyed by brand id so `satisfies` can be exhaustive. */
type BrandCopy = { tagline: string; description: string }

/**
 * One entry per brand, and `satisfies Record<BrandId, …>` so that adding a fourth profile is
 * a compile error in BOTH catalogues rather than a deployment that unfurls with no
 * description.
 *
 * `description` opens with the product's name because it is read where the name is not
 * already on screen — a search result, the line under a link preview's title. `tagline` is
 * the same claim written for a place that has already said who it is: the sign-in standfirst
 * and the social card, where the name is set in 88px serif directly above it. That is why
 * they are two entries and not one, and a translation must keep the distinction.
 */
const profiles = {
  ralph: {
    tagline: 'The governed assistant for your organization’s own knowledge.',
    description:
      'Ralph Chat — the governed enterprise assistant. Every answer shows its sources, its cost, and its work.'
  },
  evt: {
    tagline: 'EVT’s governed assistant for the knowledge your teams already hold.',
    description:
      'Ralph at EVT — the governed enterprise assistant. Every answer shows its sources, its cost, and its work.'
  },
  neoenergia: {
    tagline: 'Gi is Neoenergia’s assistant for the knowledge behind the network.',
    description: 'Gi — Neoenergia’s governed assistant. Every answer shows its sources, its cost, and its work.'
  }
} satisfies Record<BrandId, BrandCopy>

export const brand = {
  /**
   * What the reference build calls the deployment's owner, inside a sentence.
   *
   * Never a name (D-13). It is rendered only where a profile has no organisation of its own,
   * which today is the shipped, unbranded build.
   */
  organisation: 'your organization',
  profiles
}
